import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, ExternalLink, Bell } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

export default function AdminConteudos() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [contents, setContents] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', url: '', type: 'manual', notify: true });
  const [saving, setSaving] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: '', body: '' });
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastOk, setBroadcastOk] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    axios.get(`${BACKEND_URL}/api/admin/contents`, { headers }).then(r => setContents(r.data)).catch(() => {});
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/contents`, form, { headers });
      setContents(c => [res.data, ...c]);
      setForm({ title: '', description: '', url: '', type: 'manual', notify: true });
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao adicionar.'); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    setConfirm({
      message: 'Remover este conteúdo?',
      onConfirm: async () => {
        await axios.delete(`${BACKEND_URL}/api/admin/contents/${id}`, { headers });
        setContents(c => c.filter(x => x.id !== id));
      },
    });
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    setBroadcastSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/push/broadcast`, broadcast, { headers });
      setBroadcastOk(`Notificação enviada para ${res.data.sent} aluno(s).`);
      setBroadcast({ title: '', body: '' });
      setTimeout(() => setBroadcastOk(''), 4000);
    } catch { setAlertMsg('Erro ao enviar broadcast.'); }
    finally { setBroadcastSending(false); }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
  };

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold uppercase tracking-tight">Conteúdos & Manuais</h1>
        </div>

        {/* Adicionar conteúdo */}
        <div className="bg-[#121212] border border-white/5 p-6 mb-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4 flex items-center gap-2">
            <Plus size={13} /> Publicar novo conteúdo
          </h2>
          <form onSubmit={handleAdd} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Título</label>
                <input type="text" required value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className={inputCls} placeholder="Manual de Suplementação" />
              </div>
              <div>
                <label className={labelCls}>Tipo</label>
                <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={inputCls + ' cursor-pointer'}>
                  <option value="manual">Manual / Documento</option>
                  <option value="video">Vídeo</option>
                  <option value="other">Outro</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>URL do arquivo</label>
              <input type="url" required value={form.url} onChange={e => setForm(f => ({...f, url: e.target.value}))} className={inputCls} placeholder="https://srv1965-files.hstgr.io/..." />
            </div>
            <div>
              <label className={labelCls}>Descrição (opcional)</label>
              <input type="text" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className={inputCls} placeholder="Breve descrição do conteúdo" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="notify" checked={form.notify} onChange={e => setForm(f => ({...f, notify: e.target.checked}))} className="w-4 h-4 accent-white cursor-pointer" />
              <label htmlFor="notify" className="text-sm text-[#A3A3A3] cursor-pointer">Enviar notificação push para todos os alunos ativos</label>
            </div>
            <button type="submit" disabled={saving} className="self-start px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
              {saving ? 'Publicando...' : 'Publicar'}
            </button>
          </form>
        </div>

        {/* Broadcast push avulso */}
        <div className="bg-[#121212] border border-white/5 p-6 mb-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4 flex items-center gap-2">
            <Bell size={13} /> Notificação broadcast (todos os alunos)
          </h2>
          <form onSubmit={handleBroadcast} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className={labelCls}>Título</label>
              <input type="text" required value={broadcast.title} onChange={e => setBroadcast(b => ({...b, title: e.target.value}))} className={inputCls} placeholder="Aviso importante" />
            </div>
            <div className="flex-[2] min-w-[200px]">
              <label className={labelCls}>Mensagem (opcional)</label>
              <input type="text" value={broadcast.body} onChange={e => setBroadcast(b => ({...b, body: e.target.value}))} className={inputCls} placeholder="Confira a área do aluno." />
            </div>
            <button type="submit" disabled={broadcastSending} className="px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
              {broadcastSending ? 'Enviando...' : 'Enviar para todos'}
            </button>
          </form>
          {broadcastOk && <p className="mt-3 text-green-400 text-sm">{broadcastOk}</p>}
        </div>

        {/* Lista de conteúdos */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">
            Conteúdos publicados ({contents.length})
          </h2>
          {contents.length === 0 ? (
            <div className="text-center py-16 text-[#555] text-sm bg-[#121212] border border-white/5">
              Nenhum conteúdo publicado ainda.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {contents.map(c => (
                <div key={c.id} className="bg-[#121212] border border-white/5 px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{c.title}</p>
                    {c.description && <p className="text-xs text-[#555] mt-0.5 truncate">{c.description}</p>}
                    <p className="text-xs text-[#555] mt-1">{c.type} · {formatDate(c.publishedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-1 transition-colors">
                      <ExternalLink size={12} /> Abrir
                    </a>
                    <button onClick={() => handleDelete(c.id)} className="text-xs text-red-400/50 hover:text-red-400 border border-white/5 px-2 py-1 transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {alertMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#121212] border border-red-500/30 text-red-400 text-sm px-4 py-3 flex items-center gap-3 shadow-xl">
          {alertMsg}
          <button onClick={() => setAlertMsg('')} className="text-[#555] hover:text-white ml-2">✕</button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-sm">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">
                Cancelar
              </button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
